// The shared visual language of v2 fields: a bold label above a white
// rounded input, small helper text under it, the error last. Optional
// fields say so on the label — that's how a customer knows everything
// else is required without discovering it from a failed Continue.

import React from "react";
import { RegisterOptions } from "react-hook-form";

import { RenderableField } from "./types";

const INPUT_BASE =
  "block w-full rounded-md border bg-white text-base text-soil placeholder:text-oat-600 focus:border-cantelope-500 focus:outline-none focus:ring-2 focus:ring-cantelope/50";

/** The one text-input skin — 48px tall per the mockup — with the one
 * error treatment every input shares. */
export function inputClass(invalid?: boolean): string {
  return `${INPUT_BASE} h-12 px-3.5 ${invalid ? "border-red-400" : "border-oat-200"}`;
}

export function textareaClass(invalid?: boolean): string {
  return `${INPUT_BASE} min-h-[112px] resize-y px-3.5 py-3 leading-normal ${
    invalid ? "border-red-400" : "border-oat-200"
  }`;
}

export function FieldLabel({
  text,
  htmlFor,
  optional,
}: {
  text: string;
  htmlFor?: string;
  optional?: boolean;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-1.5 block text-base font-bold leading-normal text-soil"
    >
      {text}
      {optional && (
        <span className="font-normal text-oat-700"> (optional)</span>
      )}
    </label>
  );
}

export function FieldHelper({ text }: { text?: string }) {
  if (!text) return null;
  return (
    <p className="mt-1.5 text-[13px] leading-normal text-oat-700">{text}</p>
  );
}

/** Small right-aligned spinner inside an input — shown while an async
 * check (phone validity, address unit detection) is in flight. */
export function InputSpinner({ show }: { show: boolean }) {
  if (!show) return null;
  // Two spans on purpose: animate-spin's rotation would overwrite the
  // centering translate if both transforms lived on one element — the
  // spinner would slide instead of spin.
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute right-[13px] top-1/2 -translate-y-1/2"
    >
      <span className="block h-[18px] w-[18px] animate-spin rounded-full border-2 border-oat-300 border-t-evergreen" />
    </span>
  );
}

export function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-1.5 text-sm font-medium text-red-700">{message}</p>;
}

/** react-hook-form rules for the text-shaped fields. */
export function textRules(field: RenderableField): RegisterOptions {
  const rules: RegisterOptions = {};
  if (field.required) rules.required = "This is required";
  if (field.minLength) {
    rules.minLength = {
      value: field.minLength,
      message: `Must be at least ${field.minLength} characters`,
    };
  }
  if (field.maxLength) {
    rules.maxLength = {
      value: field.maxLength,
      message: `Must be at most ${field.maxLength} characters`,
    };
  }
  if (field.pattern) {
    rules.pattern = {
      value: new RegExp(field.pattern),
      message: field.patternMessage ?? "That doesn't look right",
    };
  }
  return rules;
}
